/**
 * Cart upsells slider behaviour.
 *
 * Add-to-cart works from every drawer state (empty or populated). No
 * artificial one-at-a-time rule — buttons stay tappable unless the
 * variant itself is sold out. If the fetch response fails to parse
 * (some third-party apps wrap the response in HTML), the cart drawer
 * still re-renders because we dispatch CartAddEvent unconditionally
 * and let `cart-items-component` re-fetch its own section markup.
 */

import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

class CartUpsellsController {
  constructor(root) {
    this.root = root;
    this.buttons = root.querySelectorAll('[data-upsell-add]');
    this.buttons.forEach((btn) => {
      btn.addEventListener('click', this.handleAdd.bind(this));
    });
  }

  async handleAdd(event) {
    event.preventDefault();
    const button = event.currentTarget;
    if (!button || button.disabled) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const originalText = button.textContent;
    button.classList.add('is-loading');
    button.disabled = true;
    button.textContent = '...';

    // Section IDs so the cart drawer re-renders with the new line item
    // if the response body comes back parseable.
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const cartAddUrl =
      (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) ||
      `${window.Shopify?.routes?.root || '/'}cart/add`;

    let sections = null;
    let networkOk = false;

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(cartAddUrl, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      });
      networkOk = response.ok;

      // Try to parse — but don't fail the whole flow if a third-party app
      // (e.g. Fastbundle) has intercepted and returned HTML instead of JSON.
      try {
        const data = await response.clone().json();
        if (data && data.status) {
          console.warn('[cart-upsells]', data.message || data.description);
        } else if (data && data.sections) {
          sections = data.sections;
        }
      } catch (_) {
        // Non-JSON response — that's fine, we'll still fire the event and
        // let cart-items-component re-fetch the section itself.
      }
    } catch (err) {
      console.error('[cart-upsells] network error:', err);
      button.textContent = originalText;
      button.classList.remove('is-loading');
      button.disabled = false;
      return;
    }

    // Always dispatch — cart-items-component listens and will re-fetch
    // if `sections` is missing. This is what makes the drawer refresh
    // reliably even when a third-party intercepts the response.
    document.dispatchEvent(
      new CartAddEvent({}, button.id || 'cart-upsell', {
        source: 'cart-upsell',
        productId: button.dataset.productId,
        variantId,
        itemCount: 1,
        sections,
      })
    );

    // Confirm state on the button briefly, then reset.
    button.textContent = networkOk ? '✓ Added' : originalText;
    setTimeout(() => {
      button.textContent = originalText;
      button.classList.remove('is-loading');
      button.disabled = false;
    }, 1400);
  }
}

function init() {
  document.querySelectorAll('[data-cart-upsells]').forEach((el) => {
    if (el.__upsellsController) return;
    el.__upsellsController = new CartUpsellsController(el);
  });
}

if (document.readyState !== 'loading') {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init);
}

// Re-bind when the cart drawer re-renders (its markup gets replaced).
document.addEventListener('cart:update', () => {
  setTimeout(init, 50);
});
